import { APOLLO_CLIENTS, CustomContext } from '@/graphql/client';
import type { OrderAppProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import { useProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import type {
  LanguageCodeEnum,
  ProductVariantsBySkusQuery,
} from '@/graphql/saleor/api';
import type { ContentfulData } from '@/integrations/contentful';
import { fetchProductVariantBySkus } from '@/integrations/saleor/api';
import { useRegionProvider } from '@/providers/RegionProvider';

import { getBrandConfig } from './configuration/configuration';
import {
  DETAIL_PAGE_IMAGE_SIZE,
  DETAIL_PAGE_LARGE_IMAGE_SIZE,
  NEXT_PUBLIC_IMAGES_FORMAT,
} from './const';

export const extractSaleorProductSkus = (obj: any): string[] => {
  let skus: string[] = [];
  for (const key in obj) {
    if (
      key === 'saleorProductSku' &&
      obj.saleorProductSku !== undefined &&
      obj.saleorProductSku !== null
    ) {
      skus.push(obj[key]);
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      skus = skus.concat(extractSaleorProductSkus(obj[key]));
    }
  }
  return skus;
};

export const mergeCmsDataAndProductInfo = (
  cmsData: ContentfulData[],
  productVariant?: ProductVariantsBySkusQuery | null,
  orderAppData?: OrderAppProductVariantsBusinessDetailsQuery,
): ContentfulData[] => {
  // Recursive function to replace 'saleorProductSku' with JSON value for the ProductVariant.
  const mergeProductVariant = (data: any): ContentfulData[] => {
    // Check if the current data is an array
    if (Array.isArray(data)) {
      // Map over the data and recursively call the function on each item
      return data.map((item: any) =>
        typeof item === 'object' ? mergeProductVariant(item) : item,
      );
    } else if (typeof data === 'object' && data !== null) {
      // Create a shallow copy of the object
      const newData = { ...data };
      // Check if the key matches 'saleorProductSku' and replace its value with JSON value for the ProductVariant
      if (newData.saleorProductSku) {
        const variant = productVariant?.watchs?.edges.filter(
          (variant) => variant.node.sku === newData.saleorProductSku,
        );
        const businessVariant = orderAppData?.productVariants?.filter(
          (element) => element.sku === newData.saleorProductSku,
        );
        newData.saleorProductSku = JSON.stringify({
          variant,
          businessVariant,
        });
      }
      // Iterate over the keys of the object
      Object.keys(newData).forEach((key) => {
        // If the value associated with a key is another object, recursively process that value
        if (typeof newData[key] === 'object' && newData[key] !== null) {
          newData[key] = mergeProductVariant(newData[key]);
        }
      });
      // Return the modified object
      return newData;
    } else {
      // Return the data unchanged if it's neither an array nor an object
      return data;
    }
  };
  return mergeProductVariant(cmsData);
};

export const fetchSaleorProductSkus = async (
  cmsData: ContentfulData[],
  languageCode: LanguageCodeEnum,
  channel: string | null | undefined,
) => {
  const skuArray = extractSaleorProductSkus(cmsData);
  let productVariant = null;
  if (
    skuArray &&
    skuArray.length > 0 &&
    !getBrandConfig().disableSaleorApiCall
  ) {
    const { data } = await fetchProductVariantBySkus({
      watchskus: skuArray,
      strapskus: '',
      thumbnailSize: DETAIL_PAGE_IMAGE_SIZE,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      largeMediaSize: DETAIL_PAGE_LARGE_IMAGE_SIZE,
      languageCode,
      channel,
    });
    productVariant = data;
  }
  return {
    saleorProductSkus: skuArray,
    saleorProductSkusProductVariant: productVariant,
  };
};

export const useCmsDataWithSaleorProductSkus = (
  saleorProductSkus?: string[],
  saleorProductSkusProductVariant?: ProductVariantsBySkusQuery | null,
  ...cmsDataList: ContentfulData[][]
) => {
  const { query } = useRegionProvider();

  const { data: orderAppData } = useProductVariantsBusinessDetailsQuery({
    skip: !saleorProductSkus || saleorProductSkus.length === 0,
    variables: { skus: saleorProductSkus ?? [], channel: query.channel },
    notifyOnNetworkStatusChange: true,
    context: CustomContext({
      disableErrorNotification: true,
      client: APOLLO_CLIENTS.orderApp,
    }),
  });

  return cmsDataList.map((cmsData) =>
    mergeCmsDataAndProductInfo(
      cmsData ?? [],
      saleorProductSkusProductVariant,
      orderAppData,
    ),
  );
};
